<script setup lang="ts">
import { computed } from 'vue';
import { useVipStatus } from '@/composables/useVipStatus';
import { vipTierStyle } from '@/utils/vip-tiers';

/**
 * Selo VIP do usuário logado (coroa + pílula "VIP n"), do lado do nome no
 * header mobile e do lado da logo no desktop.
 *
 * Regra: só aparece pra quem tem tag de tier DESENHADA (VIP 0 a VIP 6). Quem
 * não é VIP, é VIP sem número (`VIP_SIGNATURE`) ou está num tier que o design
 * ainda não fechou não mostra nada — `vipTierStyle` devolve `undefined` nos
 * três casos.
 *
 * CUIDADO: o tier 0 existe e é falsy. Nunca testar `if (vipTier)` aqui — quem
 * decide é o `undefined` do `vipTierStyle`.
 *
 * ENQUANTO A RESPOSTA NÃO CHEGA o componente ocupa a caixa do selo sem desenhar
 * nada (ver `TAG_ASPECT`). É o que impede o header de dar um pulo lateral no
 * meio da carga: quem está ao lado — a logo no desktop, o "Olá {nome}" no
 * mobile — é irmão de flex e andaria junto quando a arte aparecesse.
 */
const props = withDefaults(defineProps<{ height?: number }>(), { height: 20 });

/**
 * Proporção da caixa reservada, em largura/altura. As sete artes têm a MESMA
 * altura (132px) e largura entre 302 e 312 — este é o valor do meio (304/132),
 * então o pior erro é de ~1,3px na altura que o header usa. Reservar por
 * proporção, e não por px fixo, mantém a reserva certa nos dois tamanhos em que
 * o selo é usado (18 no desktop, 16 no mobile).
 *
 * Se um tier novo entrar com arte de largura muito diferente, é aqui que se
 * ajusta — o número não sai da tabela `VIP_TIERS` porque lá não há dimensão.
 */
const TAG_ASPECT = 304 / 132;

/**
 * A arte não é simétrica: a coroa sobe acima da pílula, então o centro
 * GEOMÉTRICO da imagem fica ~9,5% da altura acima do centro da pílula (medido
 * no alpha das artes: pílula em y 29–128 de 132). Centralizar a caixa deixa o
 * "VIP" visivelmente abaixo da linha do texto ao lado — este empurrão sobe a
 * imagem até o centro ÓPTICO, que é o da pílula.
 */
const PILL_OFFSET = '-9.5%';

const { isVipReady, vipTier } = useVipStatus();

const style = computed(() => vipTierStyle(vipTier.value));

/**
 * Reserva o lugar só ENQUANTO não se sabe. Depois da resposta a caixa some pra
 * quem não tem selo: manter o vão pra sempre trocaria um pulo raro (o VIP, uma
 * vez) por um buraco permanente ao lado da logo pra maioria, que não é VIP.
 *
 * Quem some ainda encolhe uma vez, mas a janela é curta e quase sempre inexiste
 * na prática: a store hidrata do memo da aba, então só a PRIMEIRA carga de cada
 * aba passa por aqui — navegar dentro do SPA já monta com a resposta na mão.
 */
const isReserving = computed(() => !isVipReady.value);

/**
 * Largura da caixa do selo na altura pedida. Serve a dois lugares:
 *
 * - o vão do `v-else-if`, que não tem imagem nenhuma pra medir;
 * - o ATRIBUTO `width` da `<img>`, junto com o `height`. O CSS mantém a largura
 *   em `auto` (cada arte tem a sua), mas antes do bitmap decodificar não existe
 *   proporção intrínseca e a imagem mede zero — o par de atributos dá ao browser
 *   uma proporção provisória e mata o segundo pulo, o de trocar o vão pela arte
 *   ainda não baixada. Quando ela chega, o `auto` assume a largura de verdade.
 */
const reservedWidth = computed(() => Math.round(props.height * TAG_ASPECT));
</script>

<template>
  <img
    v-if="style"
    :alt="style.label"
    class="block w-auto shrink-0 select-none"
    decoding="async"
    draggable="false"
    :height="props.height"
    loading="lazy"
    :src="style.tag"
    :style="{
      height: `${props.height}px`,
      transform: `translateY(${PILL_OFFSET})`,
    }"
    :width="reservedWidth"
  />

  <!-- Vão do selo enquanto o /players/me não respondeu. `aria-hidden` porque
       não há nada a anunciar: não é um selo carregando, é a ausência de
       resposta — o leitor de tela ganha o selo (ou não) quando ela chegar. -->
  <span
    v-else-if="isReserving"
    aria-hidden="true"
    class="block shrink-0"
    :style="{
      height: `${props.height}px`,
      width: `${reservedWidth}px`,
    }"
  />
</template>
